import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { checkinAPI } from '../../lib/api';
import { 
  Users,
  Clock,
  ArrowClockwise,
  User,
  Phone,
  CheckCircle,
  X,
  Clipboard,
  Stethoscope,
  SignOut,
  CaretRight
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function WaitingRoomPanel({ onCheckout }) {
  const { user } = useAuth();
  const [waitingPatients, setWaitingPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  
  // Checkout form state
  const [checkoutForm, setCheckoutForm] = useState({
    prescriptions_sent: false,
    prescriptions_notes: '',
    lab_orders_placed: false,
    lab_orders_notes: '',
    imaging_orders_placed: false,
    imaging_orders_notes: '',
    referrals_made: false,
    referrals_notes: '',
    followup_scheduled: false,
    followup_date: '',
    followup_notes: '',
    patient_education_given: false,
    education_materials: [],
    messages_sent: false,
    message_notes: '',
    additional_notes: ''
  });

  useEffect(() => {
    loadWaitingPatients();
    // Refresh every 30 seconds
    const interval = setInterval(loadWaitingPatients, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadWaitingPatients = async () => {
    try {
      const response = await checkinAPI.getWaiting();
      setWaitingPatients(response.data.waiting_patients || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        process.env.NODE_ENV === "development" && console.error('Error loading waiting patients:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (checkinId, newStatus) => {
    try {
      await checkinAPI.updateStatus(checkinId, newStatus);
      toast.success(`Patient status updated to ${newStatus.replace('_', ' ')}`);
      loadWaitingPatients();
    } catch (error) {
      toast.error('Failed to update status');
      if (process.env.NODE_ENV === 'development') {
        process.env.NODE_ENV === "development" && console.error(error);
      }
    }
  };

  const openCheckout = (patient) => {
    setSelectedPatient(patient);
    setShowCheckoutModal(true);
    // Reset form
    setCheckoutForm({
      prescriptions_sent: false,
      prescriptions_notes: '',
      lab_orders_placed: false,
      lab_orders_notes: '',
      imaging_orders_placed: false,
      imaging_orders_notes: '',
      referrals_made: false,
      referrals_notes: '',
      followup_scheduled: false,
      followup_date: '',
      followup_notes: '',
      patient_education_given: false,
      education_materials: [],
      messages_sent: false,
      message_notes: '',
      additional_notes: ''
    });
  };

  const handleCheckout = async () => {
    if (!selectedPatient) return;
    
    try {
      await checkinAPI.checkout({
        checkin_id: selectedPatient.id,
        ...checkoutForm
      });
      toast.success('Patient checked out successfully');
      setShowCheckoutModal(false);
      setSelectedPatient(null);
      loadWaitingPatients();
      if (onCheckout) onCheckout();
    } catch (error) {
      toast.error('Failed to checkout patient');
      process.env.NODE_ENV === "development" && console.error(error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'waiting': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'in_triage': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'with_provider': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'ready_for_checkout': return 'bg-teal-100 text-teal-700 border-teal-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getWaitTime = (checkInTime) => {
    const minutes = Math.floor((new Date() - new Date(checkInTime)) / 60000);
    if (minutes < 60) return `${minutes}m`;
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  };

  const statusCounts = {
    waiting: waitingPatients.filter(p => p.status === 'waiting').length,
    in_triage: waitingPatients.filter(p => p.status === 'in_triage').length,
    with_provider: waitingPatients.filter(p => p.status === 'with_provider').length,
    ready_for_checkout: waitingPatients.filter(p => p.status === 'ready_for_checkout').length,
  };

  if (loading) {
    return (
      <div className="card-clinical p-6">
        <div className="flex items-center justify-center py-8">
          <ArrowClockwise size={24} className="animate-spin text-teal-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="card-clinical p-6" data-testid="waiting-room-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users size={20} className="text-teal-500" />
          <h2 className="text-lg font-semibold text-slate-900">Waiting Room</h2>
        </div>
        <button
          onClick={loadWaitingPatients}
          className="p-2 hover:bg-slate-100 rounded-lg text-slate-500"
          title="Refresh"
        >
          <ArrowClockwise size={18} />
        </button>
      </div>

      {/* Status Summary */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        <div className="p-2 bg-amber-50 rounded-lg text-center">
          <p className="text-2xl font-bold text-amber-600">{statusCounts.waiting}</p>
          <p className="text-xs text-amber-600">Waiting</p>
        </div>
        <div className="p-2 bg-blue-50 rounded-lg text-center">
          <p className="text-2xl font-bold text-blue-600">{statusCounts.in_triage}</p>
          <p className="text-xs text-blue-600">Triage</p>
        </div>
        <div className="p-2 bg-purple-50 rounded-lg text-center">
          <p className="text-2xl font-bold text-purple-600">{statusCounts.with_provider}</p>
          <p className="text-xs text-purple-600">Provider</p>
        </div>
        <div className="p-2 bg-teal-50 rounded-lg text-center">
          <p className="text-2xl font-bold text-teal-600">{statusCounts.ready_for_checkout}</p>
          <p className="text-xs text-teal-600">Checkout</p>
        </div>
      </div>

      {/* Patient List */}
      {waitingPatients.length === 0 ? (
        <div className="text-center py-8 bg-slate-50 rounded-xl">
          <Users size={40} className="text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500">No patients checked in</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {waitingPatients.map((patient) => (
            <div 
              key={patient.id}
              className={`p-3 rounded-xl border ${getStatusColor(patient.status)}`}
              data-testid={`patient-row-${patient.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                    <User size={20} className="text-slate-400" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{patient.patient_name}</p>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {getWaitTime(patient.check_in_time)}
                      </span>
                      {patient.reason_for_visit && (
                        <span className="text-slate-500">• {patient.reason_for_visit}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons based on status */}
                <div className="flex items-center gap-1">
                  {patient.status === 'waiting' && (
                    <button
                      onClick={() => updateStatus(patient.id, 'in_triage')}
                      className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-xs flex items-center gap-1"
                      title="Call to Triage"
                    >
                      <Phone size={14} />
                      Call
                    </button>
                  )}
                  
                  {patient.status === 'in_triage' && (
                    <button
                      onClick={() => updateStatus(patient.id, 'with_provider')}
                      className="p-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-xs flex items-center gap-1"
                      title="Send to Provider"
                    >
                      <Stethoscope size={14} />
                      Provider
                    </button>
                  )}
                  
                  {patient.status === 'with_provider' && (
                    <button
                      onClick={() => updateStatus(patient.id, 'ready_for_checkout')}
                      className="p-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg text-xs flex items-center gap-1"
                      title="Ready for Checkout"
                    >
                      <CheckCircle size={14} />
                      Ready
                    </button>
                  )}
                  
                  {patient.status === 'ready_for_checkout' && (
                    <button
                      onClick={() => openCheckout(patient)}
                      className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs flex items-center gap-1"
                      title="Checkout Patient"
                      data-testid={`checkout-btn-${patient.id}`}
                    >
                      <SignOut size={14} />
                      Checkout
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckoutModal && selectedPatient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Patient Checkout</h3>
                <p className="text-sm text-slate-500">{selectedPatient.patient_name}</p>
              </div>
              <button onClick={() => setShowCheckoutModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-600 bg-amber-50 p-3 rounded-lg">
                Please verify all items before completing checkout to ensure nothing was missed.
              </p>

              {/* Prescriptions */}
              <div className="p-4 border border-slate-200 rounded-xl">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checkoutForm.prescriptions_sent}
                    onChange={(e) => setCheckoutForm({...checkoutForm, prescriptions_sent: e.target.checked})}
                    className="w-5 h-5 rounded text-teal-600"
                  />
                  <span className="font-medium">Prescriptions sent to pharmacy</span>
                </label>
                {checkoutForm.prescriptions_sent && (
                  <input
                    type="text"
                    value={checkoutForm.prescriptions_notes}
                    onChange={(e) => setCheckoutForm({...checkoutForm, prescriptions_notes: e.target.value})}
                    placeholder="Notes (optional)"
                    className="input-clinical mt-2 text-sm"
                  />
                )}
              </div>

              {/* Lab Orders */}
              <div className="p-4 border border-slate-200 rounded-xl">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checkoutForm.lab_orders_placed}
                    onChange={(e) => setCheckoutForm({...checkoutForm, lab_orders_placed: e.target.checked})}
                    className="w-5 h-5 rounded text-teal-600"
                  />
                  <span className="font-medium">Lab orders placed</span>
                </label>
                {checkoutForm.lab_orders_placed && (
                  <input
                    type="text"
                    value={checkoutForm.lab_orders_notes}
                    onChange={(e) => setCheckoutForm({...checkoutForm, lab_orders_notes: e.target.value})}
                    placeholder="Lab details (optional)"
                    className="input-clinical mt-2 text-sm"
                  />
                )}
              </div>

              {/* Imaging Orders */}
              <div className="p-4 border border-slate-200 rounded-xl">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checkoutForm.imaging_orders_placed}
                    onChange={(e) => setCheckoutForm({...checkoutForm, imaging_orders_placed: e.target.checked})}
                    className="w-5 h-5 rounded text-teal-600"
                  />
                  <span className="font-medium">Imaging/X-ray orders placed</span>
                </label>
                {checkoutForm.imaging_orders_placed && (
                  <input
                    type="text"
                    value={checkoutForm.imaging_orders_notes}
                    onChange={(e) => setCheckoutForm({...checkoutForm, imaging_orders_notes: e.target.value})}
                    placeholder="Imaging details (optional)"
                    className="input-clinical mt-2 text-sm"
                  />
                )}
              </div>

              {/* Referrals */}
              <div className="p-4 border border-slate-200 rounded-xl">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checkoutForm.referrals_made}
                    onChange={(e) => setCheckoutForm({...checkoutForm, referrals_made: e.target.checked})}
                    className="w-5 h-5 rounded text-teal-600"
                  />
                  <span className="font-medium">Referrals made</span>
                </label>
                {checkoutForm.referrals_made && (
                  <input
                    type="text"
                    value={checkoutForm.referrals_notes}
                    onChange={(e) => setCheckoutForm({...checkoutForm, referrals_notes: e.target.value})}
                    placeholder="Referral details (optional)"
                    className="input-clinical mt-2 text-sm"
                  />
                )}
              </div>

              {/* Follow-up */}
              <div className="p-4 border border-slate-200 rounded-xl">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checkoutForm.followup_scheduled}
                    onChange={(e) => setCheckoutForm({...checkoutForm, followup_scheduled: e.target.checked})}
                    className="w-5 h-5 rounded text-teal-600"
                  />
                  <span className="font-medium">Follow-up appointment scheduled</span>
                </label>
                {checkoutForm.followup_scheduled && (
                  <div className="mt-2 space-y-2">
                    <input
                      type="date"
                      value={checkoutForm.followup_date}
                      onChange={(e) => setCheckoutForm({...checkoutForm, followup_date: e.target.value})}
                      className="input-clinical text-sm"
                    />
                    <input
                      type="text"
                      value={checkoutForm.followup_notes}
                      onChange={(e) => setCheckoutForm({...checkoutForm, followup_notes: e.target.value})}
                      placeholder="Follow-up notes"
                      className="input-clinical text-sm"
                    />
                  </div>
                )}
              </div>

              {/* Patient Education */}
              <div className="p-4 border border-slate-200 rounded-xl">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checkoutForm.patient_education_given}
                    onChange={(e) => setCheckoutForm({...checkoutForm, patient_education_given: e.target.checked})}
                    className="w-5 h-5 rounded text-teal-600"
                  />
                  <span className="font-medium">Patient education materials given</span>
                </label>
              </div>

              {/* Messages/Instructions */}
              <div className="p-4 border border-slate-200 rounded-xl">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checkoutForm.messages_sent}
                    onChange={(e) => setCheckoutForm({...checkoutForm, messages_sent: e.target.checked})}
                    className="w-5 h-5 rounded text-teal-600"
                  />
                  <span className="font-medium">Messages/Instructions sent to patient</span>
                </label>
                {checkoutForm.messages_sent && (
                  <input
                    type="text"
                    value={checkoutForm.message_notes}
                    onChange={(e) => setCheckoutForm({...checkoutForm, message_notes: e.target.value})}
                    placeholder="Message summary"
                    className="input-clinical mt-2 text-sm"
                  />
                )}
              </div>

              {/* Additional Notes */}
              <div>
                <label className="label-clinical">Additional Notes</label>
                <textarea
                  value={checkoutForm.additional_notes}
                  onChange={(e) => setCheckoutForm({...checkoutForm, additional_notes: e.target.value})}
                  placeholder="Any other notes about this visit..."
                  className="input-clinical h-20"
                />
              </div>

              <button
                onClick={handleCheckout}
                className="w-full btn-teal flex items-center justify-center gap-2 py-3"
                data-testid="complete-checkout-btn"
              >
                <CheckCircle size={20} />
                Complete Checkout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
